import { Button } from '@timelyapp/react';
import type { DropdownOption } from 'components/DropdownListWithOptions';
import { Ellipsis } from 'components/Icons';
import type { ModalProps } from 'components/Modal';
import PopoverDropdownList from 'components/popovers/PopoverDropdownList';
import type { UserActionDetails } from 'components/users/UserRemoveConfirmationDialogue';
import UserRemoveConfirmationDialogue from 'components/users/UserRemoveConfirmationDialogue';
import type { ClickOutsideComponent, ClickOutsideProps } from 'hooks/useOnClickOutside';
import { withOnClickOutside } from 'hooks/useOnClickOutside';
import React, { Component } from 'react';
import type { UserPermissionsFacade } from 'redux/modules/user.types';
import type { Account } from 'types/account';
import styles from './UserActions.module.scss';

interface UserShape {
  id: number;
  deleted: boolean;
  active: boolean;
  name: string;
  avatar: string;
}

interface Props extends ClickOutsideProps<HTMLDivElement> {
  account: Account;
  currentUserId: number;
  user: UserShape;
  permissions: UserPermissionsFacade;
  modalProps?: ModalProps;
  disabled?: boolean;
  onEditClick: (userId: number) => void;
  onDeleteClick: (userId: number) => void;
  onReInviteUserClick: (userId: number) => void;
  onNotifyClick?: (userId: number) => void;
  isNotifyUserFeatureEnabled: boolean;
}

interface State {
  isOpen: boolean;
  isInviteOpen: boolean;
  userActionDetails: UserActionDetails;
}

const initialState: State = {
  isOpen: false,
  isInviteOpen: false,
  userActionDetails: null,
};

class UserActions extends Component<Props, State> implements ClickOutsideComponent {
  state = initialState;

  handleDropdownToggle = () => {
    this.setState({
      isOpen: !this.state.isOpen,
    });
  };

  handleInviteToggle = () => {
    this.setState({
      isInviteOpen: !this.state.isInviteOpen,
    });
  };

  handleClickOutside = () => {
    if (this.state.isOpen) {
      this.setState({ isOpen: false, isInviteOpen: false });
    }
  };

  handleNotifyClick = () => {
    this.handleClickOutside();
    if (this.props.onNotifyClick) {
      this.props.onNotifyClick(this.props.user.id);
    }
  };

  handleReInviteUserClick = () => {
    this.handleClickOutside();
    this.props.onReInviteUserClick(this.props.user.id);
  };

  handleEditClick = () => {
    this.handleClickOutside();
    this.props.onEditClick(this.props.user.id);
  };

  handleDeleteClick = () => {
    this.handleClickOutside();
    this.props.onDeleteClick(this.props.user.id);
  };

  handleDeleteUser = async () => {
    if (this.state.userActionDetails?.id) {
      await this.props.onDeleteClick(this.state.userActionDetails.id);

      this.setState({ userActionDetails: null });
      this.handleClickOutside();
    }
  };

  handleOptionClick = (option: DropdownOption) => {
    const { action } = option;

    if (action === 'edit') {
      this.handleEditClick();
    } else if (action === 're-invite') {
      this.handleReInviteUserClick();
    } else if (action === 'notify') {
      this.handleNotifyClick();
    } else if (action === 'delete') {
      this.setState({
        userActionDetails: {
          id: this.props.user.id,
          name: this.props.user.name,
          src: this.props.user.avatar,
        },
      });
    }
  };

  getOptions = () => {
    const { permissions, user, isNotifyUserFeatureEnabled } = this.props;
    const { canUpdate, canNotify, canDelete } = permissions;
    const options: Array<DropdownOption> = [];

    if (canUpdate.users) {
      options.push({
        action: 'edit',
        faIcon: 'pencil',
        name: 'Edit user',
      });
    }
    if (canUpdate.users && !(user.active || user.deleted)) {
      options.push({
        action: 're-invite',
        name: 'Re-invite user',
        faIcon: 'paper-plane',
      });
    }
    if (canNotify.users && user.active && isNotifyUserFeatureEnabled) {
      options.push({
        action: 'notify',
        faIcon: 'bell',
        name: 'Notify to log time',
      });
    }

    const isLoggedInUser = this.props.currentUserId === user.id;
    const isAccountOwner = user.id === this.props.account.owner_id;

    const canRemoveUser = !user.deleted && canDelete.users && !isLoggedInUser;

    if (canRemoveUser) {
      options.push({
        action: 'delete',
        name: 'Remove user',
        faIcon: 'trash',
        type: 'danger',
        disabled: isAccountOwner,
        tooltip: isAccountOwner ? 'Cannot remove the account owner' : undefined,
      });
    }
    if (!options.length) {
      return null;
    }

    return (
      <PopoverDropdownList
        onClick={this.handleOptionClick}
        targetCaret={false}
        target={
          <Button disabled={!options.length} size="small" priority="low">
            <Ellipsis />
          </Button>
        }
        options={options}
      />
    );
  };

  render() {
    const { user, disabled, permissions } = this.props;
    const disableActions = user.deleted || disabled || !permissions.canRead.users;

    if (disableActions) {
      return (
        <Button className={styles.disabledLinkButton} priority="low">
          <Ellipsis />
        </Button>
      );
    }

    return (
      <div ref={this.props.clickOutsideRef} className={styles.container}>
        {this.getOptions()}
        <UserRemoveConfirmationDialogue
          userActionDetails={this.state.userActionDetails}
          handleRemoveUser={this.handleDeleteUser}
          setUserActionDetails={userActionDetails => {
            this.setState({ userActionDetails });
          }}
        />
      </div>
    );
  }
}

export default withOnClickOutside(UserActions);
